'use client';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useTeacherOnboardingLogic } from '@hooks/useTeacherOnboardingLogic';
import { useHeaderVisibilityStore } from '@providers/header-visibility-store-provider';

import { Button } from '@components/Button';
import { ClockLoader } from '@components/ClockLoader';
import { BottomContent, PageBottom, PageContent, PageGrid } from '@components/Common';
import {
  AddressStep,
  IntroductionStep,
  NameStep,
  PhoneStep,
  PhotoStep,
} from '@components/Onboarding/TeacherOnboarding/components';

/**
 * Teacher Onboarding component for the onboarding process
 */
export const TeacherOnboarding: React.FC = () => {
  const { t } = useTranslation('common');
  const setShowBackButton = useHeaderVisibilityStore((state) => state.setShowBackButton);
  const setOnBack = useHeaderVisibilityStore((state) => state.setOnBack);

  const {
    currentStep,
    steps,
    data,
    errors,
    isLoading,
    handleBackStep,
    handleNextStep,
    setFirstName,
    setLastName,
    setPhone,
    setPostCode,
    setHouseNumber,
  } = useTeacherOnboardingLogic();

  useEffect(() => {
    // Show back button after the first step (introduction is now step 0)
    // Steps: introduction(0), uploadPhoto(1), name(2), phone(3), address(4)
    if (currentStep > 1) {
      setShowBackButton(true);
      setOnBack(handleBackStep);
    } else {
      setShowBackButton(false);
      setOnBack(undefined);
    }
    // Clean up on unmount
    return () => {
      setShowBackButton(false);
      setOnBack(undefined);
    };
  }, [currentStep, setShowBackButton, setOnBack, handleBackStep]);

  // renders the step content for the current step
  const renderStepContent = () => {
    switch (steps[currentStep]) {
      case 'introduction':
        return <IntroductionStep />;
      case 'uploadPhoto':
        return <PhotoStep />;
      case 'name':
        return (
          <NameStep
            firstName={data.firstName}
            lastName={data.lastName}
            setFirstName={setFirstName}
            setLastName={setLastName}
            errors={errors}
          />
        );
      case 'phone':
        return <PhoneStep phone={data.phone} setPhone={setPhone} error={errors.phone} />;
      case 'address':
        return (
          <AddressStep
            postCodeInput={data.postCodeInput}
            houseNumberInput={data.houseNumberInput}
            setPostCode={setPostCode}
            setHouseNumber={setHouseNumber}
            address={data.address}
            error={errors.address}
          />
        );
      default:
        return null;
    }
  };

  // renders the button option for the current step
  const getButtonOption = () => {
    // Steps: introduction(0), uploadPhoto(1), name(2), phone(3), address(4)
    const labels = [
      t('continue'),
      t('onboardingTeacher.uploadPhoto'),
      t('next'),
      t('next'),
      t('onboardingTeacher.complete'),
    ];

    return {
      label: labels[currentStep],
    };
  };

  if (isLoading) {
    return (
      <PageGrid className="full-height">
        <PageContent>
          <ClockLoader />
        </PageContent>
      </PageGrid>
    );
  }

  const buttonOption = getButtonOption();

  return (
    <PageGrid className="full-height">
      <PageContent>{renderStepContent()}</PageContent>
      <PageBottom>
        <BottomContent>
          <Button
            label={buttonOption?.label}
            type="button"
            onClick={handleNextStep}
          />
        </BottomContent>
      </PageBottom>
    </PageGrid>
  );
};
